// ContactPage: contact form + Google Map
import React, { useState } from "react";
import contactApi from "../api/contact";
import AlertMessage from "../components/AlertMessage";
import AnimatedPage from "../components/AnimatedPage";
import SEO from "../components/SEO";

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    // Clear field error on change
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.name?.trim()) {
      newErrors.name = "Name is required";
    } else if (form.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    if (!form.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!form.message?.trim()) {
      newErrors.message = "Message is required";
    } else if (form.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setStatus(null);
    setIsLoading(true);
    try {
      const res = await contactApi.submitContact(form);
      setStatus({
        type: "success",
        msg: res.data.message || "Message sent successfully!",
      });
      setForm({ name: "", email: "", subject: "", message: "" });
      setErrors({});
    } catch (err) {
      setStatus({
        type: "error",
        msg: err.response?.data?.message || "Failed to send message.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const inputClass = (field) =>
    `w-full rounded-md border px-3 py-2 text-sm dark:bg-slate-800 dark:text-slate-100 ${
      errors[field]
        ? "border-red-400 dark:border-red-500"
        : "border-slate-300 dark:border-slate-700"
    }`;

  return (
    <AnimatedPage>
      <SEO
        title="Contact Us - Get in Touch"
        description="Contact Moroccan Desert Trek for questions about tours, bookings, or custom itineraries. We're here to help you plan your perfect Morocco adventure."
        path="/contact"
      />
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <h1 className="mb-4 text-2xl font-bold">Contact Us</h1>
          {status && (
            <div className="mb-3">
              <AlertMessage type={status.type}>{status.msg}</AlertMessage>
            </div>
          )}
          <form
            onSubmit={handleSubmit}
            className="space-y-3 rounded-xl border border-slate-200 p-4 dark:border-slate-800"
          >
            <div>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Name"
                required
                maxLength={255}
                className={inputClass("name")}
              />
              {errors.name && (
                <p className="mt-1 text-xs text-red-500">{errors.name}</p>
              )}
            </div>
            <div>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Email"
                required
                maxLength={255}
                className={inputClass("email")}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-500">{errors.email}</p>
              )}
            </div>
            <div>
              <input
                name="subject"
                value={form.subject}
                onChange={handleChange}
                placeholder="Subject (optional)"
                maxLength={255}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
              />
            </div>
            <div>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows={4}
                placeholder="Message"
                required
                maxLength={5000}
                className={inputClass("message")}
              />
              {errors.message && (
                <p className="mt-1 text-xs text-red-500">{errors.message}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-md bg-emerald-600 px-4 py-2 font-medium text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Sending..." : "Send"}
            </button>
          </form>
        </div>
        <div>
          <h2 className="mb-4 text-xl font-semibold">Where to find us</h2>
          <div className="h-80 w-full overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800">
            <iframe
              title="Google Map"
              src="https://maps.google.com/maps?q=marrakech&t=&z=11&ie=UTF8&iwloc=&output=embed"
              className="h-full w-full"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </AnimatedPage>
  );
}
